import React, { useState } from "react";
import { Select } from "../../Components/Select";
import { TextInput } from "../../Components/TextInput";
import { Label } from "../../Components/Label";
import { sanitiseString } from "../../Utils";

import type { IconTypes } from "../../Icons";
import type { IconPosition } from "../../Types/IconPosition";

export type FilterValue = string | number;

export interface SearchAndFilterOption {
  value: FilterValue;
  text: string;
  disabled?: boolean;
}

export interface FilterConfig {
  label: React.ComponentProps<typeof Label>;
  options: SearchAndFilterOption[];
}

export interface SearchConfig {
  label: React.ComponentProps<typeof Label>;
  buttonText: string;
  buttonIconType?: IconTypes;
  buttonIconPosition?: IconPosition;
  defaultValue?: string;
}

export interface Props {
  id: string;
  dataTestId: string;
  search: SearchConfig;
  filter: FilterConfig;
  onSearch: (searchTerm: string, filterValue: FilterValue) => void;
  onFilterChange?: (searchTerm: string, filterValue: FilterValue) => void;
  ariaLabel?: string;
  classes?: string;
}

export const SearchAndFilter = (props: Props) => {
  const sanitisedId: string = sanitiseString(props.id);
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);

  const [searchTerm, setSearchTerm] = useState<string>(
    props.search.defaultValue ?? "",
  );
  const [filterValue, setFilterValue] = useState<FilterValue>(
    props.filter.options[0].value,
  );

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (value: FilterValue): void => {
    setFilterValue(value);
    props.onFilterChange?.(searchTerm, value);
  };

  const handleSearch = (): void => {
    props.onSearch(searchTerm, filterValue);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    handleSearch();
  };

  return (
    <form
      id={sanitisedId}
      role="search"
      aria-label={props.ariaLabel ?? "Search and filter"}
      className={props.classes}
      data-testid={sanitisedDataTestId}
      onSubmit={handleSubmit}
    >
      <div className="ons-grid">
        <div
          className="ons-grid__col ons-col-4@s ons-u-mb-s@xxs@s"
          data-testid={`${sanitisedDataTestId}-filter-col`}
        >
          <Select
            id={`${sanitisedId}-filter`}
            dataTestId={`${sanitisedDataTestId}-filter`}
            label={props.filter.label}
            options={props.filter.options}
            variants="wide"
            onChange={handleFilterChange}
          />
        </div>
        <div
          className="ons-grid__col ons-col-8@s"
          data-testid={`${sanitisedDataTestId}-search-col`}
        >
          <TextInput
            id={`${sanitisedId}-search`}
            dataTestId={`${sanitisedDataTestId}-search`}
            label={props.search.label}
            value={searchTerm}
            onChange={handleSearchTermChange}
            searchButton={{
              text: props.search.buttonText,
              type: "submit",
              iconType: props.search.buttonIconType,
              iconPosition: props.search.buttonIconPosition ?? "only",
              onClick: handleSearch,
            }}
          />
        </div>
      </div>
    </form>
  );
};
